import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { Bell, CheckCheck } from 'lucide-react'
import { keys, notificationsQuery } from '@/api/queries'
import { rpc } from '@/api/rpc'
import type { AppNotification } from '@/api/types'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { FollowUpActions } from '@/features/notifications/follow-up-actions'
import { cn } from '@/lib/cn'
import { formatWhen } from '@/lib/format'

export const Route = createFileRoute('/_app/notifications')({ component: Inbox })

function Inbox() {
  const { data, isLoading } = useQuery(notificationsQuery)
  const queryClient = useQueryClient()
  const router = useRouter()
  const markRead = useMutation({
    mutationFn: (ids: string[] | null) => rpc('mark_notifications_read', { ids }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.notifications }),
  })
  const unread = (data ?? []).filter((n) => !n.read_at)

  const open = (n: AppNotification) => {
    if (!n.read_at) markRead.mutate([n.id])
    // Links can carry a search part (e.g. /queue?relux=...), which `navigate({ to })` would drop.
    if (n.url) router.history.push(n.url)
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-3xl uppercase">Notifications</h1>
        {unread.length > 0 && (
          <Button variant="outline" onClick={() => markRead.mutate(null)}>
            <CheckCheck aria-hidden /> Mark all read
          </Button>
        )}
      </div>
      {isLoading && <p className="text-fg-muted">Loading...</p>}
      {!isLoading && (data?.length ?? 0) === 0 && (
        <EmptyState icon={Bell} title="No notifications yet" />
      )}
      <ul className="flex flex-col gap-2">
        {(data ?? []).map((n) => {
          const actions = (n.payload.actions as string[] | undefined) ?? []
          return (
            <li
              key={n.id}
              className={cn(
                'rounded-card border bg-surface p-4',
                n.read_at ? 'border-border' : 'border-2 border-brand',
              )}
            >
              <button type="button" onClick={() => open(n)} className="block w-full text-left">
                <span className="flex items-start justify-between gap-3">
                  <span className="font-semibold">{n.title}</span>
                  <span className="shrink-0 text-sm text-fg-muted">{formatWhen(n.created_at)}</span>
                </span>
                {n.body && (
                  <span className="mt-1 block whitespace-pre-line text-fg-muted">{n.body}</span>
                )}
              </button>
              {n.type === 'follow_up' &&
                actions.length > 0 &&
                typeof n.payload.reminder_id === 'string' && (
                  <div className="mt-3">
                    <FollowUpActions
                      reminderId={n.payload.reminder_id}
                      reluxId={(n.payload.relux_id as string | undefined) ?? null}
                      actions={actions}
                      onAnswered={() => markRead.mutate([n.id])}
                    />
                  </div>
                )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
